import type { ProtocolStep } from "@src/packages/injection-protocol/types";

/**
 * Which diagram is drawn over her own video, for the step she is on.
 *
 * The figure is chosen here rather than declared on the step, so the clinical
 * package keeps no opinion about presentation. `injection-protocol` describes
 * what the procedure is; this describes what a drawing of it looks like. A new
 * protocol can arrive without an illustrator, and an illustration can change
 * without touching content that was clinically reviewed.
 *
 * **The figure illustrates the step's copy. It never adds an instruction.**
 * Every angle, distance and duration a figure depicts has to be one the step
 * already states in words — a diagram is read faster than a sentence and would
 * otherwise become the instruction. `figures.test.ts` holds the mapping, and the
 * rule is why `inject` draws the 90° the copy names and nothing about how long
 * to hold.
 */

export type OverlayFigure =
  /** A clock: this step is about when, not how. */
  | "timing"
  /** Hands and a clean surface, before anything is handled. */
  | "hygiene"
  /** The device itself — cap, barrel, the part she is asked to look at. */
  | "device"
  /** A vial being mixed, or drawn from — powder, diluent and a syringe. */
  | "mix"
  /** Where on the body, marked relative to the navel. */
  | "site"
  /** The upper outer gluteal quadrants, without choosing a side. */
  | "gluteal-site"
  /** Flat tissue and a withdrawn plunger, only where aspiration is stated. */
  | "aspiration-inject"
  /** The injection: the pinch, and the angle the copy names. */
  | "inject"
  /**
   * The injection with no angle mark: the pinch and the needle, nothing else.
   *
   * For a label that says "straight into your skin as shown by your healthcare
   * provider" and no degrees. The square the `inject` figure draws is a
   * right-angle mark, and a figure may depict only what its step states.
   */
  | "inject-straight"
  /**
   * The injection at 45°: the pinch and the needle leaning, with the arc the
   * copy names.
   *
   * For a label that says "fully insert the syringe needle at a 45-degree
   * angle". The right-angle square would contradict it and the straight
   * drawing would hide it.
   */
  | "inject-angled"
  /** After the needle is out: pressure, and the sharps bin. */
  | "aftercare";

/**
 * Matched on the last segment of the step id, which is the step's name.
 *
 * Ids are shaped `<medication>.<device>.<name>` — `ovidrel.pen.clean-site` —
 * so the name is stable across devices and markets while the prefix is not.
 */
const FIGURE_BY_STEP_ID: Readonly<Record<string, OverlayFigure>> = {
  "pregnyl.im.clean-site": "gluteal-site",
  "pregnyl.im.inject": "aspiration-inject",
  // The Rekovelle label names no angle, so its injection is drawn without one.
  "rekovelle.pen.inject": "inject-straight",
  // The Pergoveris pen's label says "push the needle into the skin entirely"
  // and no angle; its vial's leaflet says 45° to 90° and keeps the mark.
  "pergoveris.pen.inject": "inject-straight",
  // The Decapeptyl 0.1 mg leaflet states the route and no angle.
  "decapeptyl.vial.inject": "inject-straight",
  // The Ozempic pen's label says "as your doctor or nurse has shown you".
  "ozempic.pen.inject": "inject-straight",
  // The Mounjaro KwikPen instructions say "insert the needle into your skin".
  "mounjaro.pen.inject": "inject-straight",
  "mounjaro.single-dose-pen.inject": "inject-straight",
  "mounjaro.vial.inject": "inject-straight",
  "wegovy.pen.inject": "inject-straight",
  "wegovy.single-dose-pen.inject": "inject-straight",
  "wegovy.syringe.inject": "inject-angled",
  "zepbound.single-dose-pen.inject": "inject-straight",
  "zepbound.pen.inject": "inject-straight",
  "zepbound.vial.inject": "inject-straight",
  "zepbound.multi-dose-vial.inject": "inject-straight",
  // The Saxenda pen's label says "as your doctor or nurse has shown you".
  "saxenda.pen.inject": "inject-straight",
  "victoza.pen.inject": "inject-straight",
};

const FIGURE_BY_STEP_NAME: Readonly<Record<string, OverlayFigure>> = {
  "critical-timing": "timing",
  prepare: "hygiene",
  // Washing hands and laying the kit out: the same picture as preparing.
  "gather-supplies": "hygiene",
  // Reconstitution and drawing up are the same picture: a vial and a syringe.
  // Neither is the pen the `device` figure draws, and a pen shown over "inject
  // the diluent into the powder vial" would be a different device than the one
  // in her hands.
  reconstitute: "mix",
  // Drawing the solvent out of its own vial, before any powder is touched.
  "draw-diluent": "mix",
  // The second, third and sixth vials, dissolved into the syringe that already
  // holds the first. Still a vial and a syringe.
  "reconstitute-additional": "mix",
  // Counting the vials her clinic prescribed, with the kit in front of her.
  // `verify` would draw a clock, and this step is not about when.
  "how-many-vials": "mix",
  // Which route these steps are for. Nothing is in her hands yet and nothing
  // has been mixed, so `site` — where on the body — is the honest drawing; the
  // `verify` fallback would put a clock on a step that is not about when.
  route: "site",
  // Reading the strength printed on the glass. What she is looking at is the
  // vial, which is what `mix` draws.
  "check-strength": "mix",
  // Screwing the reconstitution needle onto the solvent syringe. What is in her
  // hands is the syringe, which is what `device` draws.
  "attach-needle": "device",
  // Pushing the solvent through the stopper, and swirling until it clears: a
  // vial and a syringe, both times.
  "add-solvent": "mix",
  dissolve: "mix",
  // Letting the plunger rise on its own against the vial's over-pressure. Still
  // a vial and a syringe, and still nothing near her skin.
  plunger: "mix",
  // What this walk assumes about her vial, and the day it stops being medicine.
  // Neither is about when she injects, so the `verify` clock would mislead.
  "later-days": "mix",
  expired: "mix",
  // Pulling the solvent into the syringe before it goes into the powder: the
  // same vial-and-syringe picture as the mixing it leads to.
  "draw-up-the-solvent": "mix",
  "draw-up-the-dose": "mix",
  // Drawing from a vial into a syringe is the same picture as mixing one.
  "draw-dose": "mix",
  // Swapping the mixing needle for the fine one. The vial is done with by then,
  // so `mix` would show her a container she has already put down; what is in
  // her hand is the syringe, which is what `device` draws.
  "change-the-needle": "device",
  // Flicking the bubbles to the top and pushing them out: the vial is behind
  // her by then and what is in her hand is the syringe.
  "remove-air-bubbles": "device",
  // Reading the number in the pen's window, after the needle is out. What is in
  // her hand is the pen, which is what `device` draws — not the injection she
  // has just finished.
  "inspect-dose-window": "device",
  // Dialling a number on a pen: what is in her hand is the device.
  "set-the-dose": "device",
  "ready-the-pen": "device",
  // Holding the pen up and watching for the drop. What is in her hand is the
  // pen and nothing has touched her skin yet, so `device` — the `verify` kind
  // it would otherwise fall back to draws a clock, and this step is not about
  // when.
  prime: "device",
  "ready-the-syringe": "device",
  // Screwing the needle onto a prefilled syringe, and pushing the droplet out
  // of it: the syringe is the thing in her hand both times.
  "attach-the-needle": "device",
  "check-for-a-droplet": "device",
  inspect: "device",
  // Pulling the needle out and firing the safety sleeve: the syringe in hand.
  "remove-the-needle": "device",
  "clean-site": "site",
  inject: "inject",
  aftercare: "aftercare",
};

/**
 * The fallback, used when a protocol names a step this file has not seen.
 *
 * Keyed by `kind`, which every step has and which the type system guarantees is
 * one of four values — so an unillustrated step still gets a figure that is
 * true of it rather than no figure at all. A missing drawing must never be the
 * reason a step renders differently from its neighbours.
 */
const FIGURE_BY_KIND: Readonly<Record<ProtocolStep["kind"], OverlayFigure>> = {
  verify: "timing",
  prepare: "hygiene",
  irreversible: "inject",
  aftercare: "aftercare",
};

/** The step's name — the last dot-separated segment of its id. */
export function stepName(stepId: string): string {
  const segments = stepId.split(".");
  return segments[segments.length - 1] ?? stepId;
}

export function figureForStep(step: ProtocolStep): OverlayFigure {
  return (
    FIGURE_BY_STEP_ID[step.id] ??
    FIGURE_BY_STEP_NAME[stepName(step.id)] ??
    FIGURE_BY_KIND[step.kind]
  );
}

/**
 * Whether this step's figure was chosen for the step itself.
 *
 * Exported for the test that asserts every Ovidrel step is illustrated
 * deliberately rather than landing on the `kind` fallback. The fallback is a
 * safety net, not a destination, and without this the net would hide the gap.
 */
export function hasDedicatedFigure(step: ProtocolStep): boolean {
  return (
    step.id in FIGURE_BY_STEP_ID || stepName(step.id) in FIGURE_BY_STEP_NAME
  );
}
